import { Alert, AlertTitle } from '@mui/material';
import { DialogActions, DialogContent, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useSwitchSpaceActionStyles } from './switch-space-action.styles';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { SwitchSpaceActionProps, useSwitchSpaceAction } from './switch-space-action.hook';
import { Yes as YesIcon } from '../../../../../ui/components/pure/icons/Yes.icon';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { LoadingIndicator } from '../../../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { FormControl } from '../../../../../ui/components/pure/form-control/form-control.component';
import { Dropdown } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { useMutationSwitchSpace } from '../../deliveries-details.queries';
import { Colors } from '../../../../../ui/theme/colors';

export function SwitchSpaceModal(props: Omit<SwitchSpaceActionProps, 'onSubmit'> & { deliveryID: string }) {
  const mutation = useMutationSwitchSpace(props.deliveryID);

  function onSubmit(
    deliveryContainerID: string,
    options: {
      onSuccess(): void;
      onError(): void;
    }
  ) {
    mutation.mutate(deliveryContainerID, {
      onSuccess: () => {
        options.onSuccess();
      },
      onError: () => {
        options.onError();
      }
    });
  }

  const { form, handlers, state, hubContainerSelectOptions, selectedDeliveryContainer, content } = useSwitchSpaceAction(
    { ...props, onSubmit }
  );

  return (
    <SwitchSpaceModalComponent
      form={form}
      selectedDeliveryContainer={selectedDeliveryContainer}
      handlers={handlers}
      content={content}
      state={state}
      hubContainerSelectOptions={hubContainerSelectOptions}
    />
  );
}

type ModalProps = ReturnType<typeof useSwitchSpaceAction>;

export function SwitchSpaceModalComponent({
  form,
  state,
  content,
  selectedDeliveryContainer,
  hubContainerSelectOptions,
  handlers
}: ModalProps) {
  switch (state.current) {
    case 'CONFIRMATION':
      return (
        <Modal title={content.title} maxWidth="xs" open onClose={handlers.onClose}>
          <ConfirmationView
            form={form}
            handlers={handlers}
            state={state}
            content={content}
            hubContainerSelectOptions={hubContainerSelectOptions}
          />
        </Modal>
      );
    case 'SENDING_REQUEST':
      return (
        <Modal title={content.title} maxWidth="xs" open onClose={handlers.onClose}>
          <SendingRequestView content={content} />
        </Modal>
      );
    case 'ERROR':
      return (
        <Modal title={content.title} maxWidth="xs" open onClose={handlers.onClose}>
          <ErrorView state={state} handlers={handlers} content={content} />
        </Modal>
      );
    case 'SUCCESS':
      return (
        <Modal title={content.title} maxWidth="xs" open onClose={handlers.onClose}>
          <SuccessView selectedDeliveryContainer={selectedDeliveryContainer} handlers={handlers} content={content} />
        </Modal>
      );
    default:
      return null;
  }
}

function ConfirmationView({
  form,
  handlers,
  content,
  hubContainerSelectOptions
}: Pick<ModalProps, 'handlers' | 'state' | 'hubContainerSelectOptions' | 'form' | 'content'>) {
  const classes = useSwitchSpaceActionStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <form noValidate autoComplete="off" onSubmit={form.handleSubmit(handlers.onSubmit)}>
      <DialogContent className={classes.dialogContent}>
        <p style={{ marginBottom: '8px' }}>{content.promptMessage}</p>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl
              control={form.control}
              name="deliveryContainerID"
              required={true}
              Component={Dropdown}
              ComponentProps={{
                required: true,
                fullWidth: true,
                textFieldDesktopWidth: '100%',
                dropdownVariant: 'floating' as const,
                placeholder: 'Stored in space',
                menuItems: hubContainerSelectOptions
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <TextButton color="secondary" variant="outlined" fontSize="1rem" fullWidth={isXs} onClick={handlers.onClose}>
          {content.cancelLabel}
        </TextButton>
        <TextButton
          type="submit"
          className={classes.actionButton}
          color="primary"
          fontSize="1rem"
          variant="outlined"
          fullWidth={isXs}
          // Override the color and border color to match the design
          style={{
            color: Colors.collectedGreen,
            borderColor: Colors.collectedGreen
          }}
        >
          {content.callToActionLabel}
        </TextButton>
      </DialogActions>
    </form>
  );
}

function SendingRequestView({ content }: Pick<ModalProps, 'content'>) {
  const classes = useSwitchSpaceActionStyles();

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogContentFullHeightCentered}>
          <LoadingIndicator size="small" />
          <p className={classes.dialogContentSpinnerTitle}>
            {content.actionLabelLine1} <br />
            {content.actionLabelLine2}
          </p>
        </div>
      </DialogContent>
      <DialogActions className={[classes.dialogActions, classes.dialogActionsEmpty].join(' ')}></DialogActions>
    </>
  );
}

function SuccessView({
  selectedDeliveryContainer,
  handlers,
  content
}: Pick<ModalProps, 'selectedDeliveryContainer' | 'handlers' | 'content'>) {
  const classes = useSwitchSpaceActionStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogContentFullHeightCentered}>
          <YesIcon />
          <p className={classes.dialogContentSpinnerTitle}>{selectedDeliveryContainer?.name || 'space'}</p>
          <p className={classes.dialogContentSpinnerMessage}>{content.successMessage}</p>
        </div>
      </DialogContent>
      <DialogActions className={[classes.dialogActions, classes.dialogActionsEmpty].join(' ')}>
        <TextButton
          type="submit"
          className={classes.actionButton}
          fullWidth={isXs}
          color="primary"
          fontSize="1rem"
          variant="outlined"
          onClick={handlers.onClose}
        >
          {content.successLabel}
        </TextButton>
      </DialogActions>
    </>
  );
}

function ErrorView({ state, handlers, content }: Pick<ModalProps, 'state' | 'handlers' | 'content'>) {
  const classes = useSwitchSpaceActionStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const error =
    'error' in state
      ? state.error
      : {
          title: 'An error occurred while sending your request',
          details: 'Please close the modal and try again'
        };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.dialogContentFullHeight}>
          {error ? (
            <p>
              <Alert variant="outlined" severity="error">
                <AlertTitle>{error.title}</AlertTitle>
                {error.details}
              </Alert>
            </p>
          ) : null}
        </div>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton fullWidth={isXs} color="secondary" variant="outlined" fontSize="1rem" onClick={handlers.onClose}>
          {content.closeLabel}
        </TextButton>
      </DialogActions>
    </>
  );
}
